import * as React from "react";

/**
 * Box Elements asset loader utility
 * Ensures Box CSS and JS are loaded before Box components are rendered
 */

interface BoxAssetLoader {
  loadBoxAssets(): Promise<void>;
  isBoxLoaded(): boolean;
}

class BoxAssetLoaderImpl implements BoxAssetLoader {
  private loadPromise: Promise<void> | null = null;
  private isLoaded = false;

  loadBoxAssets(): Promise<void> {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = this.loadAssets();
    return this.loadPromise;
  }

  isBoxLoaded(): boolean {
    return this.isLoaded && typeof window !== "undefined" && window.Box;
  }

  private async loadAssets(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (typeof window !== "undefined" && window.Box) {
        this.isLoaded = true;
        resolve();
        return;
      }

      const totalAssets = 4; // 2 CSS + 2 JS
      let loadedAssets = 0;

      const checkComplete = () => {
        loadedAssets++;
        if (loadedAssets === totalAssets) {
          this.isLoaded = true;
          resolve();
        }
      };

      const handleError = (error: any) => {
        reject(new Error(`Failed to load Box assets: ${error}`));
      };

      // Load CSS files
      const explorerCSS = document.createElement("link");
      explorerCSS.rel = "stylesheet";
      explorerCSS.href =
        "https://cdn01.boxcdn.net/platform/elements/19.0.0/en-US/explorer.css";
      explorerCSS.crossOrigin = "anonymous";
      explorerCSS.onload = checkComplete;
      explorerCSS.onerror = handleError;
      document.head.appendChild(explorerCSS);

      const pickerCSS = document.createElement("link");
      pickerCSS.rel = "stylesheet";
      pickerCSS.href =
        "https://cdn01.boxcdn.net/platform/elements/19.0.0/en-US/picker.css";
      pickerCSS.crossOrigin = "anonymous";
      pickerCSS.onload = checkComplete;
      pickerCSS.onerror = handleError;
      document.head.appendChild(pickerCSS);

      // Load JS files
      const explorerJS = document.createElement("script");
      explorerJS.src =
        "https://cdn01.boxcdn.net/platform/elements/19.0.0/en-US/explorer.js";
      explorerJS.crossOrigin = "anonymous";
      explorerJS.onload = checkComplete;
      explorerJS.onerror = handleError;
      document.body.appendChild(explorerJS);

      const pickerJS = document.createElement("script");
      pickerJS.src =
        "https://cdn01.boxcdn.net/platform/elements/19.0.0/en-US/picker.js";
      pickerJS.crossOrigin = "anonymous";
      pickerJS.onload = checkComplete;
      pickerJS.onerror = handleError;
      document.body.appendChild(pickerJS);
    });
  }
}

// Export singleton instance
export const boxAssetLoader = new BoxAssetLoaderImpl();

// React hook for Box components
export function useBoxAssets() {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (boxAssetLoader.isBoxLoaded()) {
      setIsLoaded(true);
      return;
    }

    setIsLoading(true);
    boxAssetLoader
      .loadBoxAssets()
      .then(() => {
        setIsLoaded(true);
        setError(null);
      })
      .catch((err) => {
        setError(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  return { isLoaded, isLoading, error };
}
